import { concatMap, delay, map, mergeMap, retryWhen, takeUntil, tap } from 'rxjs/operators';

import { AfterViewInit, Component, OnInit, OnDestroy, inject } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Container } from '../container';
import { DisableSubmit } from '../utils/disable-submit';
import { Server } from '../server';
import { User } from '../user';
import { UserService } from '../user.service';
import { Observable, of, OperatorFunction, Subject, Subscription, throwError } from 'rxjs';
import { Token } from '../token';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { FeatureFlagsService } from '../feature-flags.service';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { DescriptionService } from '../descriptions/description.service';
import { DescriptionData, NotebookNotebook } from './notebook-description';

@Component({
  selector: 'naas-container-selector',
  templateUrl: './container-selector.component.html',
  styleUrls: ['./container-selector.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class ContainerSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  submit = new DisableSubmit('Start', 'Starting...');
  shutdown = new DisableSubmit('Shut Down', 'Shutting Down...');
  user: User;
  containers: Container[] = [];
  server: Server | null;
  errorMessage?: string;
  connecting = false;
  progressText: string = '';
  form: UntypedFormGroup;
  descriptionData: DescriptionData | null = null;
  selectedNotebook: NotebookNotebook | null = null;
  handledDeepLinkLaunch = false;
  serverStatusSubscription: Subscription;
  private launchCancelled$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private featureFlagService: FeatureFlagsService,
    private route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private http: HttpClient,
    private descriptionService: DescriptionService,
  ) {
    const titleService = inject(Title);
    const pageTitle = 'Start a Notebook Server - Noteable';
    titleService.setTitle(pageTitle);

    const myH1 = document.querySelector('h1');
    if (myH1) {
      myH1.textContent = pageTitle;
    }
  }

  openDetails() {
    const description_modal = document.getElementById('description_modal') as HTMLDialogElement;
    description_modal.showModal();
    document.body.style.overflow = 'hidden'; // Prevent scrolling when the popup is open
  }

  closeDetails() {
    const description_modal = document.getElementById('description_modal') as HTMLDialogElement;
    description_modal.close();
    document.body.style.overflow = ''; // Restore scrolling when the popup is closed
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      select: this.updateContainerSelect(),
    });

    this.route.data.subscribe((data: { user: User; containers: Container[]; server: Server | null }) => {
      this.user = data.user;
      this.containers = data.containers.sort((x1, x2) => x1.display_name.localeCompare(x2.display_name));
      this.server = data.server;

      if (this.user.quota_percentage >= 90) {
        this.errorMessage =
          'You have used ' +
          this.user.quota_percentage.toFixed(2) +
          '% of your storage quota. Please delete unnecessary files.';
      }
      if (this.user.quota_percentage >= 100) {
        this.errorMessage =
          'You have used ' +
          this.user.quota_percentage.toFixed(2) +
          '% of your storage quota, and your notebook may struggle to start.';
      }

      // This makes the UI behave a bit nicer and remembers your selection in each different mode.
      this.form.patchValue({
        select: this.updateSelectedContainer().id.toString(),
      });

      // If the user refreshes the launch page during container start up, this will resume the
      // spinning start button and connect them automatically to their notebook.
      if (this.isServerSpawning()) {
        if (this.hasExistingServer()) {
          this.resumeExistingServer();
        } else {
          this.startServer();
        }
      } else if (this.shouldAutoLaunchDeepLinkedNotebook()) {
        this.handleDeepLinkLaunch();
      }

      this.descriptionService.getDescription().subscribe(data => {
        this.descriptionData = data;
        this.updateSelectedNotebookDescription();
      });

      // Update the selected notebook description when the select dropdown changes
      this.form.get('select')?.valueChanges.subscribe(() => {
        this.updateSelectedNotebookDescription();
      });
    });
  }

  ngAfterViewInit() {
    // This makes the UI behave a bit nicer and remembers your selection in each different mode.

    const selectDropdown = document.getElementById('containerSelect') as HTMLSelectElement;
    if (selectDropdown !== null && typeof selectDropdown !== 'undefined') {
      this.form.patchValue({
        select: this.updateSelectedContainer().id.toString(),
      });
    }
  }

  ngOnDestroy() {
    // It's important to clean up subscriptions
    this.cancelLaunch();
    this.launchCancelled$.complete();
    this.serverStatusSubscription?.unsubscribe();
  }
  /**
   * Updates the description of the currently selected notebook based on the user's selection in the dropdown.
   * Retrieves the selected notebook ID from the form, finds the corresponding container, and then matches it
   * with the notebook entry to set the appropriate description. If no matching container or description data is found,
   * the method exits early without updating the description.
   */
  updateSelectedNotebookDescription() {
    const selectedId = parseInt(this.form.get('select')?.value, 10);
    if (!this.descriptionData) return;

    const selectedContainer = this.containers.find(c => c.id === selectedId);
    if (!selectedContainer) return;

    const matchedEntry = this.descriptionData.notebooks.find(
      (entry: NotebookNotebook) => entry.display_name === selectedContainer.display_name,
    );

    this.selectedNotebook = matchedEntry ?? null;
  }

  isServerLive() {
    return (
      this.server?.reconnectable === true || this.server?.status === 'Running' || this.hasReadyServerStatusCondition()
    );
  }

  private hasReadyServerStatusCondition(): boolean {
    const status = this.server?.status;
    return Array.isArray(status) && status.some(condition => /^Ready:\s*True\b/i.test(condition.trim()));
  }

  isServerSpawning() {
    return this.server ? this.server.status === 'Pending' || this.server.status === 'Unschedulable' : false;
  }

  isServerTerminating() {
    return this.server ? this.server.status === 'Terminating' : false;
  }

  hasExistingServer() {
    return (
      this.server?.deployment_exists === true ||
      this.server?.pod_exists === true ||
      this.isServerLive() ||
      this.isServerTerminating() ||
      this.server?.status_check_failed === true ||
      Array.isArray(this.server?.status)
    );
  }

  canStopServer() {
    return this.hasExistingServer() && !this.isServerTerminating();
  }

  canCancelStartup() {
    return this.isConnecting() && !this.isServerLive() && this.canStopServer();
  }

  showStartButtons() {
    const hasExplicitExistingServer = this.server?.deployment_exists === true || this.server?.pod_exists === true;
    return (
      (!this.hasExistingServer() || (this.isServerSpawning() && !hasExplicitExistingServer)) &&
      !this.isServerTerminating()
    );
  }

  isUserUpdating() {
    return this.user.is_updating;
  }

  isConnecting() {
    return this.connecting;
  }

  getServerStatusText(fallback: string): string {
    const status = this.server?.status;
    if (Array.isArray(status)) {
      return status.join('\n');
    }
    return status ?? fallback;
  }

  trackServerStatus() {
    // Unsubscribe if already subscribed to prevent multiple subscriptions
    this.serverStatusSubscription?.unsubscribe();

    // Subscribe to the server status observable
    this.serverStatusSubscription = this.userService.getServerDetail().subscribe(server => {
      this.server = server;
      const message = this.getServerStatusText('Starting Notebook Server (trackServerStatus)...');
      this.progressText = message;
    });
  }

  startServer() {
    this.cancelLaunch();
    this.submit.setSubmitting(true);
    this.connecting = true;
    const selectedContainer = this.getSelectedContainer();
    if (!selectedContainer) {
      this.onError('Please select a notebook to start.', 'start');
      return;
    }

    return this.userService
      .startServer({ id: selectedContainer.id })
      .pipe(
        tap(() => (this.progressText = this.getServerStatusText('Starting Notebook Server...'))),
        this.waitUntilStarted$(),
        this.waitUntilReady$(),
        mergeMap(() => this.userService.reconnect()),
        takeUntil(this.launchCancelled$),
        tap(token => (window.location.href = this.getOpenNotebookUrl(token.token))),
      )
      .subscribe({
        next: () => this.onSuccess(),
        error: e => this.onError(e, 'start'),
      });
  }

  reconnect() {
    this.connecting = true;
    this.userService
      .reconnect()
      .pipe(
        tap(res => {
          window.location.href = this.getOpenNotebookUrl(res.token);
        }),
      )
      .subscribe();
  }

  resumeExistingServer() {
    this.cancelLaunch();
    this.submit.setSubmitting(true);
    this.connecting = true;
    return this.userService
      .reconnect()
      .pipe(
        this.waitUntilStarted$(),
        this.waitUntilReady$(),
        mergeMap(() => this.userService.reconnect()),
        takeUntil(this.launchCancelled$),
        tap(token => (window.location.href = this.getOpenNotebookUrl(token.token))),
      )
      .subscribe({
        next: () => this.onSuccess(),
        error: e => this.onError(e, 'start'),
      });
  }

  stopServer() {
    this.cancelLaunch();
    return this.stopNotebookServer('Notebook Servers can take up to 30 seconds to shutdown');
  }

  cancelStartup() {
    this.cancelLaunch();
    return this.stopNotebookServer('Notebook startup cancellation can take up to 30 seconds');
  }

  private stopNotebookServer(progressText: string) {
    this.shutdown.setSubmitting(true);

    return this.userService
      .stopServer()
      .pipe(
        tap(() => (this.progressText = progressText)),
        this.waitUntilStopped$(),
      )
      .subscribe({
        next: () => this.onSuccess(),
        error: e => this.onError(e, 'stop'),
      });
  }

  updateContainerSelect() {
    try {
      return this.updateSelectedContainer().id.toString();
    } catch {
      return 0; //If no notebooks are found, default to 0
    }
  }

  updateSelectedContainer(): Container {
    const selectedNotebook = this.user.notebook;
    return (
      this.containers.find(c => c.container_name === selectedNotebook || c.display_name === selectedNotebook) ||
      this.containers.find(c => this.server !== null && c.container_name === this.server.latest_container) ||
      this.containers.find(c => c.display_name === 'Standard Notebook (Python 3)') ||
      this.containers[0]
    );
  }

  private getSelectedContainer(): Container | undefined {
    const selectedId = parseInt(this.form.get('select')?.value, 10);
    return this.containers.find(container => container.id === selectedId);
  }

  private getDeepLinkedContainer(): Container | undefined {
    if (this.user?.notebook == null || this.user.notebook === '' || this.user.notebook === 'no_notebook') {
      return undefined;
    }
    return this.containers.find(container => container.container_name === this.user.notebook);
  }

  private shouldAutoLaunchDeepLinkedNotebook(): boolean {
    return !this.handledDeepLinkLaunch && this.getDeepLinkedContainer() !== undefined;
  }

  private isCurrentServerForDeepLinkedCourse(containerName: string): boolean {
    return this.server?.latest_container === containerName && this.server?.active_course_id === this.user?.course_code;
  }

  private handleDeepLinkLaunch() {
    const deepLinkedContainer = this.getDeepLinkedContainer();
    if (!deepLinkedContainer) {
      return;
    }

    this.handledDeepLinkLaunch = true;
    this.form.patchValue({ select: deepLinkedContainer.id.toString() });

    if (this.isServerLive()) {
      if (this.isCurrentServerForDeepLinkedCourse(deepLinkedContainer.container_name)) {
        this.reconnect();
        return;
      }
      this.startServer();
      return;
    }

    if (this.hasExistingServer()) {
      return;
    }

    if (!this.isServerTerminating()) {
      this.startServer();
    }
  }

  private getNotebookUrl(token: string): string {
    return '/user/' + this.user.uuid + '/?token=' + encodeURIComponent(token);
  }

  private getNotebookReadinessUrl(token: string): string {
    return '/user/' + this.user.uuid + '/api/status?token=' + encodeURIComponent(token);
  }

  /**
   *
   * @param token
   * @private
   *
   * This is a hack to make the RStudio container start in RStudio rather than Lab
   * hopefully this can be removed in future and replaced with getNotebookUrl
   */
  private getOpenNotebookUrl(token: string): string {
    const name = this.server?.latest_container ?? '';
    if (name.includes('rstudio')) {
      return '/user/' + this.user.uuid + '/rstudio?token=' + encodeURIComponent(token);
    }
    return this.getNotebookUrl(token);
  }

  /**
   * Starts a notebook/container and waits for the backend to say its running
   */
  private waitUntilStarted$(): OperatorFunction<Token, Token> {
    return mergeMap(token =>
      this.userService.getServerDetail().pipe(
        tap(server => {
          this.server = server;
          if (this.server.status === 'Unschedulable') {
            this.progressText =
              'Our servers are currently at full capacity. ' + 'Your Notebook Server will start as soon as possible.';
          } else {
            this.progressText = this.getServerStatusText('Starting Notebook Server....');
          }
        }),
        map(() => {
          const liveness = this.isServerLive();
          if (liveness === false || liveness === undefined || liveness === null) {
            throw new Error();
          }
          return token;
        }),
        retryWhen(errors => this.handleError(errors)),
      ),
    );
  }

  /**
   * Waits until the notebook/container accepts connections. This sometimes
   * takes some time due to the ingress resource needing to wait until the container
   * is fully running before it routes correctly. This server request will return a 500/502/504
   * error until the ingress controller is routing to the notebook. It will then return a 200
   * and when it does the users browser can safely be redirected there.
   */
  private waitUntilReady$(): OperatorFunction<Token, Token> {
    return mergeMap(token =>
      this.userService.testNotebookStatus(this.getNotebookReadinessUrl(token.token)).pipe(
        tap(response => {
          if (this.isNotebookReadyResponse(response)) {
            this.connecting = false;
            return;
          }
          throw new Error('Notebook Server not started yet');
        }),
        map(() => token),
        retryWhen(errors => errors.pipe(delay(1000))), // retry *ALL* errors, even 50x, 40x etc
      ),
    );
  }

  private isNotebookReadyResponse(response: HttpResponse<string>): boolean {
    if (response.ok !== true) {
      return false;
    }
    if (this.isNotebookLoginUrl(response.url)) {
      return false;
    }
    if (this.isNotebookLoginPage(response.body ?? '')) {
      return false;
    }
    return true;
  }

  private isNotebookLoginUrl(url: string | null): boolean {
    if (url === null || url === '') {
      return false;
    }
    const loginPath = '/user/' + this.user.uuid + '/login';
    try {
      return new URL(url, window.location.origin).pathname === loginPath;
    } catch {
      return url.includes(loginPath);
    }
  }

  private isNotebookLoginPage(body: string): boolean {
    const normalisedBody = body.toLowerCase();
    return (
      normalisedBody.includes('<form') &&
      normalisedBody.includes('login') &&
      (normalisedBody.includes('name="password"') ||
        normalisedBody.includes("name='password'") ||
        normalisedBody.includes('password_input'))
    );
  }

  /**
   * Waits until a notebook/container has shutdown
   */
  private waitUntilStopped$(): OperatorFunction<number, Server> {
    return mergeMap(() =>
      this.userService.getServerDetail().pipe(
        tap(server => (this.server = server)),
        map(server => {
          if (this.isServerTerminating()) {
            throw new Error();
          }
          return server;
        }),
        retryWhen(errors => this.handleError(errors)),
      ),
    );
  }

  /**
   * For HttpErrorResponses:
   * Handles retries for 20x messages but throws an error for anything else.
   * The API returns pending etc status for some operations so we retry them until
   * the pending goes away. If we get an HTTP error then we stop retrying and display the
   * error message.
   * For Errors (eg throw Error(...)):
   * If the message is blank we continue and retry, otherwise we display the error message.
   *
   * @param errors
   */
  private handleError(errors: Observable<HttpErrorResponse | Error>) {
    return errors.pipe(
      concatMap(e => {
        // Avoid error retries for Http status codes 40x & 50x
        // Eg, e must be a proper HttpErrorResponse with a non-200
        if (e instanceof HttpErrorResponse && !e.ok) {
          // Been an actual error so don't retry anymore, display an error
          // return throwError(e);
          return throwError(() => new Error(e.message));
        }
        if (e.message !== undefined && e.message !== '') {
          return throwError(() => new Error(e.message));
        }
        // Not an HTTP error so do the retry, eg we're still waiting on the container
        // This Error type is generated above with a throw
        return of(e);
      }),
      delay(2000),
    );
  }

  private cancelLaunch() {
    this.launchCancelled$.next();
  }

  /**
   * Reset state when submit/shutdown is finished normally
   */
  private onSuccess() {
    this.errorMessage = undefined;
    this.submit.setSubmitting(false);
    this.shutdown.setSubmitting(false);
    this.progressText = '';
  }

  /**
   * Reset state/display an error when submit/shutdown fails for some reason
   *
   * @param error
   * @param verb
   */
  private onError(error: HttpErrorResponse | string, verb: 'start' | 'stop') {
    if (error instanceof HttpErrorResponse) {
      this.errorMessage =
        error.error !== undefined && error.error.message !== undefined
          ? error.error.message
          : `Unable to ${verb} your Notebook. Please refresh this page and try again later.`;
    } else {
      this.errorMessage = error;
    }
    this.submit.setSubmitting(false);
    this.shutdown.setSubmitting(false);
    this.progressText = '';
    this.server = null;
  }
}
